import { useEffect, useState, useMemo, useCallback, useRef } from "react";

import { useParams, useLocation } from "wouter";
import { apiUrl } from "@/lib/apiUrl";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Loader2, CheckCircle2, AlertCircle, Info, Check as CheckIcon } from "lucide-react";
import {
  answerKey as buildAnswerKey,
  countValidAnswers,
  findFirstIncompleteModuleIndex,
  getMissingGlobalQuestions,
  getMissingModuleQuestions,
  isGlobalComplete,
  isIdentificationComplete,
  isModuleComplete,
  validateSubmission,
} from "@shared/questionnaireValidation";

/**
 * FIX MOBILE: Detecta se o dispositivo é mobile/touch para usar <select> nativo
 * Evita bugs de ghost-click, delay 300ms e teclado virtual do Radix UI em iOS/Android
 */
function useIsMobile(): boolean {
  return useMemo(() => {
    if (typeof window === 'undefined') return false;
    return (
      /Mobi|Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
      window.matchMedia('(pointer: coarse)').matches
    );
  }, []);
}
/**
 * FASE 1 - Aplicação Pública do Questionário
 * V35b - White-label: DUAS logos (canal + cliente) + cores dinâmicas + fallback resiliente
 * 
 * Rota: /q/:publicToken
 * Acesso: Público (sem autenticação)
 * 
 * Funcionalidades:
 * - Renderização do questionário conforme template congelado
 * - Seleção obrigatória de Setor → Cargo
 * - Persistência local de progresso (localStorage)
 * - 1 submissão por dispositivo por campanha
 * - Barra de progresso e navegação por módulos
 * - White-label: DUAS logos (canal + cliente) + cores primária/acento dinâmicas
 * - Fallback resiliente: sem quadrado branco quando logo falha
 */

interface Answer {
  moduleCode: string;
  questionCode: string;
  answerScore: number;
  answerLabelSnapshot: string;
  answerText?: string;
}

interface LocalProgress {
  sector: string;
  jobTitle: string;
  answers: Answer[];
  lastSaved: string;
}

interface Branding {
  companyName: string | null;
  companyLogoUrl: string | null;
  channelName: string | null;
  channelLogoUrl: string | null;
  primaryColor: string;
  accentColor: string;
}

const DEFAULT_BRANDING: Branding = {
  companyName: null,
  companyLogoUrl: null,
  channelName: null,
  channelLogoUrl: null,
  primaryColor: '#002F59',
  accentColor: '#C0FF00',
};

/**
 * Calcula cor de foreground (branco ou preto) com base na luminância da cor de fundo.
 */
function contrastForeground(hex: string): string {
  const c = hex.replace('#', '');
  if (c.length !== 6) return '#FFFFFF';
  const r = parseInt(c.substring(0, 2), 16) / 255;
  const g = parseInt(c.substring(2, 4), 16) / 255;
  const b = parseInt(c.substring(4, 6), 16) / 255;
  const lum = 0.2126 * r + 0.7152 * g + 0.0722 * b;
  return lum > 0.45 ? '#1A1A1A' : '#FFFFFF';
}

/**
 * Componente de logo com skeleton loading e fallback resiliente.
 * - Mostra shimmer enquanto carrega
 * - Oculta completamente (colapsa) se a imagem falhar ou não existir
 * - Não mostra quadrado branco em nenhum cenário
 */
function BrandLogo({
  src,
  alt,
  className = '',
}: {
  src: string | null;
  alt: string;
  className?: string;
}) {
  const [status, setStatus] = useState<'loading' | 'loaded' | 'error'>('loading');

  useEffect(() => {
    if (!src) {
      setStatus('error');
      return;
    }
    setStatus('loading');
    const img = new Image();
    img.onload = () => setStatus('loaded');
    img.onerror = () => setStatus('error');
    img.src = src;
    return () => {
      img.onload = null;
      img.onerror = null;
    };
  }, [src]);

  // Não renderizar nada se não tem URL ou falhou
  if (!src || status === 'error') return null;

  // Skeleton shimmer enquanto carrega
  if (status === 'loading') {
    return (
      <div
        className={`bg-white/20 rounded animate-pulse flex-shrink-0 ${className}`}
        style={{ width: 120, height: 40 }}
      />
    );
  }

  // Imagem carregada com sucesso
  return (
    <img
      src={src}
      alt={alt}
      className={`object-contain flex-shrink-0 ${className}`}
      onError={() => setStatus('error')}
    />
  );
}

export default function QuestionnairePublic() {
  const isMobile = useIsMobile();
  const { publicToken: rawToken } = useParams<{ publicToken: string }>();
  const [, setLocation] = useLocation();

  // Resolver token truncado: se o token tiver menos de 64 chars, buscar o token completo por prefixo
  const isTokenTruncated = !!rawToken && rawToken.length < 64;
  const { data: resolvedData, isLoading: isResolvingToken, error: resolveError } =
    trpc.questionnaire.public.resolveToken.useQuery(
      { partialToken: rawToken || "" },
      { enabled: isTokenTruncated && (rawToken?.length || 0) >= 10, retry: false }
    );

  // Token efetivo: se truncado e resolvido, usar o token completo; caso contrário, usar o raw
  const publicToken = isTokenTruncated
    ? (resolvedData?.resolvedToken || "")
    : (rawToken || "");

  // Se o token foi resolvido com sucesso, redirecionar para a URL com o token completo
  useEffect(() => {
    if (resolvedData?.wasResolved && resolvedData.resolvedToken) {
      setLocation(`/q/${resolvedData.resolvedToken}`, { replace: true });
    }
  }, [resolvedData, setLocation]);

  // FIX MOBILE: ref para forçar blur antes de abrir dropdown (fecha teclado virtual)
  const sectorTriggerRef = useRef<HTMLButtonElement>(null);
  const jobTitleTriggerRef = useRef<HTMLButtonElement>(null);

  // Estado do formulário
  const [deviceId, setDeviceId] = useState<string>("");
  const [sector, setSector] = useState<string>("");
  const [jobTitle, setJobTitle] = useState<string>("");
  const [answers, setAnswers] = useState<Record<string, Answer>>({});
  const [currentModuleIndex, setCurrentModuleIndex] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [showAlreadyAnsweredModal, setShowAlreadyAnsweredModal] = useState(false);
  // Debounce: evita cliques múltiplos rápidos nos botões de navegação
  // Aumentado para 600ms para dar tempo ao Radix UI fechar portais (removeChild fix)
  const [isNavigating, setIsNavigating] = useState(false);
  const navigationDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // PJC-94: ao tentar avançar com pendências, destaca o que falta e explica o motivo
  const [showErrors, setShowErrors] = useState(false);
  const [blockMessage, setBlockMessage] = useState<string | null>(null);

  // Token pronto para uso (64 chars)
  const tokenReady = publicToken.length >= 64;

  // Buscar contexto da campanha
  const { data: context, isLoading, error } = trpc.questionnaire.public.getContext.useQuery(
    { publicToken: publicToken || "" },
    { enabled: tokenReady, retry: false }
  );

  // Buscar setores da empresa
  const { data: sectorsData, isLoading: isLoadingSectors } = trpc.questionnaire.public.listSectorsByToken.useQuery(
    { publicToken: publicToken || "" },
    { enabled: tokenReady, retry: false }
  );

  // Buscar cargos do setor selecionado
  const { data: jobsData, isLoading: isLoadingJobs } = trpc.questionnaire.public.listJobsByTokenAndSector.useQuery(
    { publicToken: publicToken || "", sector: sector },
    { enabled: tokenReady && !!sector, retry: false }
  );

  const submitMutation = trpc.questionnaire.public.submitAnswers.useMutation();

  // PJC-94: acesso ao contexto dentro do efeito de retomada sem recriá-lo a cada refetch
  const contextRef = useRef(context);
  contextRef.current = context;

  // V35b: Extrair branding com fallback
  const branding: Branding = useMemo(() => {
    if (!context?.branding) return DEFAULT_BRANDING;
    return { ...DEFAULT_BRANDING, ...context.branding };
  }, [context?.branding]);

  const primaryFg = contrastForeground(branding.primaryColor);

  // V35b: Verificar se há pelo menos uma logo para exibir
  const hasAnyLogo = !!(branding.channelLogoUrl || branding.companyLogoUrl);

  // V35b: CSS custom properties para white-label
  const brandingStyle = useMemo(() => ({
    '--wl-primary': branding.primaryColor,
    '--wl-primary-fg': primaryFg,
    '--wl-accent': branding.accentColor,
  } as React.CSSProperties), [branding.primaryColor, branding.accentColor, primaryFg]);

  // Gerar ou recuperar deviceId
  useEffect(() => {
    if (!context) return;
    const storageKey = "questionnaire_device_id";
    let storedDeviceId = localStorage.getItem(storageKey);
    if (context.campaign.allowMultipleResponsesPerDevice) {
      storedDeviceId = `device_${Date.now()}_${Math.random().toString(36).substring(2, 15)}`;
    } else {
      if (!storedDeviceId) {
        storedDeviceId = `device_${Date.now()}_${Math.random().toString(36).substring(2, 15)}`;
        localStorage.setItem(storageKey, storedDeviceId);
      }
    }
    setDeviceId(storedDeviceId);
  }, [context]);

  // Carregar progresso salvo
  useEffect(() => {
    if (!publicToken || !deviceId) return;
    const storageKey = `questionnaire_progress_${publicToken}_${deviceId}`;
    const savedProgress = localStorage.getItem(storageKey);
    if (savedProgress) {
      try {
        const progress: LocalProgress = JSON.parse(savedProgress);
        setSector(progress.sector || "");
        setJobTitle(progress.jobTitle || "");
        const answersMap: Record<string, Answer> = {};
        progress.answers.forEach((a) => {
          answersMap[`${a.moduleCode}_${a.questionCode}`] = a;
        });
        setAnswers(answersMap);

        // PJC-94: retoma no primeiro módulo incompleto (respeitando as mesmas regras de bloqueio)
        const ctx = contextRef.current as any;
        if (ctx?.modules?.length) {
          const idx = findFirstIncompleteModuleIndex(ctx.modules, answersMap);
          const unlocked =
            isIdentificationComplete(progress.sector || "", progress.jobTitle || "") &&
            isGlobalComplete(ctx.globalQuestions || [], answersMap);
          setCurrentModuleIndex(unlocked ? Math.min(idx, ctx.modules.length - 1) : 0);
        }
      } catch (e) {
        console.error("Failed to load progress:", e);
      }
    }
  }, [publicToken, deviceId]);

  // Salvar progresso localmente
  const saveProgressLocally = useCallback(() => {
    if (!publicToken || !deviceId) return;
    const storageKey = `questionnaire_progress_${publicToken}_${deviceId}`;
    const progress: LocalProgress = {
      sector,
      jobTitle,
      answers: Object.values(answers),
      lastSaved: new Date().toISOString(),
    };
    localStorage.setItem(storageKey, JSON.stringify(progress));
  }, [publicToken, deviceId, sector, jobTitle, answers]);

  // Salvar progresso automaticamente quando responder
  useEffect(() => {
    if (Object.keys(answers).length > 0) {
      saveProgressLocally();
    }
  }, [answers, sector, jobTitle, saveProgressLocally]);

  // Navegação com debounce (300ms) para evitar cliques duplos que causam race condition no DOM
  const handleNavigate = useCallback((newIndex: number) => {
    if (isNavigating || isSubmitting) return;
    // Limpar timeout anterior se existir
    if (navigationDebounceRef.current) {
      clearTimeout(navigationDebounceRef.current);
    }
    setIsNavigating(true);
    // Aguardar 100ms antes de trocar módulo (dá tempo ao Radix UI fechar portais)
    setTimeout(() => {
      setCurrentModuleIndex(newIndex);
    }, 100);
    // Liberar após 600ms — tempo suficiente para o Radix UI fechar portais e o DOM estabilizar
    navigationDebounceRef.current = setTimeout(() => {
      setIsNavigating(false);
      navigationDebounceRef.current = null;
    }, 600);
  }, [isNavigating, isSubmitting]);

  // Enviar respostas (V49: somente submissão completa, sem salvar progresso parcial)
  const handleSubmit = async () => {
    if (!publicToken || !deviceId || !sector || !jobTitle) {
      alert("Por favor, selecione setor e cargo");
      return;
    }
    setIsSubmitting(true);
    try {
      await submitMutation.mutateAsync({
        publicToken,
        deviceId,
        sector,
        jobTitle,
        answers: Object.values(answers),
        isComplete: true, // V49: sempre completo
      });
      setSubmitSuccess(true);
      const storageKey = `questionnaire_progress_${publicToken}_${deviceId}`;
      localStorage.removeItem(storageKey);
    } catch (error: any) {
      if (error.message === "QUESTIONNAIRE_ALREADY_ANSWERED") {
        setShowAlreadyAnsweredModal(true);
      } else if (typeof error.message === "string" && error.message.startsWith("QUESTIONNAIRE_INCOMPLETE")) {
        // Defesa em profundidade do backend: no fluxo normal o botão já bloqueia antes do envio
        alert("Responda todas as perguntas para continuar.");
      } else {
        // NUNCA exibir mensagem técnica — apenas mensagem amigável
        alert("Ocorreu um problema ao enviar suas respostas. Tente novamente em alguns instantes. Se o problema persistir, entre em contato com o RH da sua empresa.");
        // Logar erro no servidor silenciosamente
        try {
          fetch(apiUrl("/api/trpc/systemErrors.logPublicError"), {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              json: {
                errorType: "QUESTIONNAIRE",
                errorMessage: `[SUBMIT_ERROR] ${error.message || "Unknown error"}`.substring(0, 2000),
                errorStack: error.stack?.substring(0, 10000),
                url: window.location.href.substring(0, 512),
                userAgent: navigator.userAgent.substring(0, 512),
                context: { action: "submitAnswers", sector, jobTitle, timestamp: new Date().toISOString() },
              },
            }),
          }).catch(() => {});
        } catch {}
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Loading
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
          <p className="mt-4 text-gray-600">Carregando questionário...</p>
        </div>
      </div>
    );
  }

  // Erro — NUNCA exibir mensagem técnica
  if (error || !context) {
    // Determinar mensagem amigável baseada no tipo de erro
    const isExpired = error?.message?.includes('CAMPAIGN_EXPIRED') || error?.message?.includes('CAMPAIGN_NOT_ACTIVE');
    const isNotFound = error?.message?.includes('NOT_FOUND') || error?.message?.includes('INVALID');
    const friendlyMessage = isExpired
      ? "O prazo para responder este questionário já encerrou."
      : isNotFound
        ? "Este link não é válido ou a campanha não está mais disponível."
        : "Ocorreu um problema ao carregar o questionário. Tente recarregar a página. Se o problema persistir, entre em contato com o RH da sua empresa.";

    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <Card className="max-w-md w-full">
          <CardHeader>
            <div className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-destructive" />
              <CardTitle>{isExpired ? "Questionário Encerrado" : isNotFound ? "Link Inválido" : "Erro ao Carregar"}</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-gray-600">{friendlyMessage}</p>
            {!isExpired && !isNotFound && (
              <Button variant="outline" onClick={() => window.location.reload()} className="w-full">
                Recarregar página
              </Button>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  // Sucesso
  if (submitSuccess) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4" style={brandingStyle}>
        <Card className="max-w-md w-full">
          {/* V35b: Logos na tela de sucesso */}
          {hasAnyLogo && (
            <div className="flex items-center justify-center gap-6 pt-6 px-6">
              <BrandLogo
                src={branding.channelLogoUrl}
                alt={`Logo ${branding.channelName || 'Canal'}`}
                className="h-10 max-w-[140px]"
              />
              <BrandLogo
                src={branding.companyLogoUrl}
                alt={`Logo ${branding.companyName || 'Empresa'}`}
                className="h-10 max-w-[140px]"
              />
            </div>
          )}
          <CardHeader>
            <div className="flex items-center gap-2 justify-center">
              <CheckCircle2 className="h-6 w-6" style={{ color: branding.primaryColor }} />
              <CardTitle>Respostas Enviadas!</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="text-center">
            <p className="text-gray-600">
              Obrigado por responder o questionário. Suas respostas foram registradas com sucesso.
            </p>
            <p className="mt-4 text-sm text-gray-500">
              Este questionário é anônimo e suas respostas foram utilizadas exclusivamente para análise estatística agregada.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const { campaign, template, modules, globalQuestions } = context;
  const sectors = sectorsData?.sectors || [];
  const jobs = jobsData?.jobs || [];

  const modulesList = modules || [];
  const currentModule = modules[currentModuleIndex];
  const globalQuestionsList = globalQuestions || [];

  // Calcular progresso
  const totalModuleQuestions = modules.reduce((sum: number, m: any) => sum + (m.questions?.length || 0), 0);
  const totalGlobalQuestions = globalQuestionsList.length;
  const totalQuestions = totalModuleQuestions + totalGlobalQuestions;
  // PJC-94: conta só respostas válidas do template atual (texto curto/apagado e chaves antigas não contam)
  const answeredQuestions = countValidAnswers({ globalQuestions: globalQuestionsList, modules: modulesList }, answers);
  const progress = totalQuestions > 0 ? (answeredQuestions / totalQuestions) * 100 : 0;

  // PJC-94: preenchimento sequencial - cada etapa só libera a seguinte quando está completa
  const identOk = isIdentificationComplete(sector, jobTitle);
  const globalOk = isGlobalComplete(globalQuestionsList, answers);
  const modulesUnlocked = identOk && globalOk;
  const currentModuleOk = !!currentModule && isModuleComplete(currentModule, answers);
  const canAdvance = modulesUnlocked && currentModuleOk;
  const allAnswered = identOk && validateSubmission({ globalQuestions: globalQuestionsList, modules: modulesList }, answers).ok;
  const isLastModule = currentModuleIndex >= modulesList.length - 1;
  const blocked = isLastModule ? !allAnswered : !canAdvance;
  const missingGlobalCodes = new Set(getMissingGlobalQuestions(globalQuestionsList, answers).map((q: any) => q.code));
  const missingModuleCodes = new Set(
    currentModule ? getMissingModuleQuestions(currentModule, answers).map((q: any) => q.code) : []
  );
  const globalMinLength = Math.max(0, ...globalQuestionsList.map((q: any) => q.minLength || 0));

  const scrollToId = (id: string) => {
    setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "center" }), 0);
  };

  // Explica o motivo do bloqueio, destaca as pendências e leva a pessoa até a primeira
  const explainBlock = () => {
    setShowErrors(true);
    if (!identOk) {
      setBlockMessage("Selecione setor e cargo para continuar.");
      scrollToId("pjc94-identification");
    } else if (!globalOk) {
      setBlockMessage(
        globalMinLength > 0
          ? `Preencha a descrição de atividades (mínimo de ${globalMinLength} caracteres) para continuar.`
          : "Responda as perguntas iniciais para continuar."
      );
      scrollToId("pjc94-global");
    } else {
      setBlockMessage("Responda todas as perguntas para continuar.");
      // Último módulo com pendência em outro módulo (ex.: rascunho restaurado): volta ao primeiro incompleto
      const firstIncomplete = findFirstIncompleteModuleIndex(modulesList, answers);
      if (isLastModule && currentModuleOk && firstIncomplete < modulesList.length) {
        setCurrentModuleIndex(firstIncomplete);
        return;
      }
      const first = currentModule ? getMissingModuleQuestions(currentModule, answers)[0] : null;
      if (first) scrollToId(`q-${buildAnswerKey(currentModule.code, first.code)}`);
    }
  };

  const goNext = () => {
    if (blocked) {
      explainBlock();
      return;
    }
    setShowErrors(false);
    setBlockMessage(null);
    handleNavigate(currentModuleIndex + 1);
  };

  const submitIfValid = () => {
    if (blocked) {
      explainBlock();
      return;
    }
    setShowErrors(false);
    setBlockMessage(null);
    handleSubmit();
  };

  const errorRing = "rounded-lg border-2 border-red-400 bg-red-50/40 p-3";

  return (
    <>
    <div className="min-h-screen bg-gray-50" style={brandingStyle}>
      {/* V35b: Branded Header com DUAS logos */}
      <div
        className="w-full py-4 px-4"
        style={{ backgroundColor: branding.primaryColor }}
      >
        <div className="max-w-3xl mx-auto flex items-center gap-3">
          {/* Logo do Canal (esquerda) */}
          <BrandLogo
            src={branding.channelLogoUrl}
            alt={`Logo ${branding.channelName || 'Canal'}`}
            className="h-9 md:h-10 max-w-[140px] md:max-w-[160px]"
          />

          {/* Título central */}
          <div className="flex-1 min-w-0 text-center">
            <h1
              className="text-base md:text-lg font-semibold truncate"
              style={{ color: primaryFg }}
            >
              Questionário de Saúde Mental
            </h1>
            {(branding.companyName || branding.channelName) && (
              <p
                className="text-xs md:text-sm truncate opacity-80"
                style={{ color: primaryFg }}
              >
                {branding.companyName || branding.channelName}
              </p>
            )}
          </div>

          {/* Logo do Cliente (direita) */}
          <BrandLogo
            src={branding.companyLogoUrl}
            alt={`Logo ${branding.companyName || 'Empresa'}`}
            className="h-9 md:h-10 max-w-[140px] md:max-w-[160px]"
          />
        </div>
      </div>

      <div className="max-w-3xl mx-auto py-8 px-4">
        {/* Info Card */}
        <Card className="mb-6">
          <CardContent className="pt-6">
            <Alert>
              <Info className="h-4 w-4" />
              <AlertDescription>
                Este questionário é anônimo e você pode pausar e continuar depois no mesmo dispositivo. Apenas uma resposta é permitida por dispositivo. Suas respostas serão utilizadas exclusivamente para análise estatística agregada.
              </AlertDescription>
            </Alert>
          </CardContent>
        </Card>

        {/* Seleção de Setor e Cargo — FIX MOBILE v2 */}
        <Card id="pjc94-identification" className={`mb-6 ${showErrors && !identOk ? "border-2 border-red-400" : ""}`}>
          <CardHeader>
            <CardTitle>Identificação Organizacional</CardTitle>
            <CardDescription>Selecione seu setor e cargo (obrigatório)</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* CAMPO SETOR */}
            <div>
              <Label htmlFor="sector" className="text-sm font-medium mb-1 block">Setor *</Label>
              {isLoadingSectors ? (
                <div className="flex items-center gap-2 p-3 text-sm text-gray-500 border rounded-md bg-gray-50">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Carregando setores...
                </div>
              ) : isMobile ? (
                /* FIX MOBILE: <select> nativo — elimina ghost-click, delay 300ms e bug do teclado virtual */
                <div className="relative">
                  <select
                    id="sector"
                    value={sector}
                    onChange={(e) => {
                      const val = e.target.value;
                      setSector(val);
                      setJobTitle("");
                    }}
                    className="w-full h-12 px-3 pr-10 rounded-md border border-input bg-background text-sm appearance-none focus:outline-none focus:ring-2 focus:ring-ring"
                    style={{ touchAction: 'manipulation', WebkitTapHighlightColor: 'transparent' }}
                  >
                    <option value="">Selecione o setor</option>
                    {sectors.map((s: string) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                  {/* Ícone chevron customizado */}
                  <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center">
                    <svg className="h-4 w-4 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                  {/* Badge de confirmação visual quando selecionado */}
                  {sector && (
                    <div className="mt-1.5 flex items-center gap-1.5 text-xs font-medium" style={{ color: branding.primaryColor }}>
                      <CheckIcon className="h-3.5 w-3.5" />
                      <span>Setor selecionado: <strong>{sector}</strong></span>
                    </div>
                  )}
                </div>
              ) : (
                /* Desktop: Radix UI Select (comportamento original) */
                <Select
                  value={sector}
                  onValueChange={(value) => {
                    setSector(value);
                    setJobTitle("");
                  }}
                >
                  <SelectTrigger id="sector" ref={sectorTriggerRef}>
                    <SelectValue placeholder="Selecione o setor" />
                  </SelectTrigger>
                  <SelectContent
                    // FIX: z-index alto garante que o conteúdo fique acima de qualquer overlay
                    className="z-[9999]"
                    // FIX: position=popper evita que o dropdown fique atrás de elementos fixos
                    position="popper"
                    sideOffset={4}
                  >
                    {sectors.length === 0 && (
                      <SelectItem value="_empty" disabled>
                        Nenhum setor disponível
                      </SelectItem>
                    )}
                    {sectors.map((s: string) => (
                      <SelectItem key={s} value={s}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              {!isLoadingSectors && sectors.length === 0 && (
                <Alert className="mt-2">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    Nenhum setor disponível. Peça ao RH para realizar a importação de cargos e setores antes de responder.
                  </AlertDescription>
                </Alert>
              )}
            </div>

            {/* CAMPO CARGO */}
            <div>
              <Label htmlFor="jobTitle" className="text-sm font-medium mb-1 block">Cargo *</Label>
              {isLoadingJobs && sector ? (
                <div className="flex items-center gap-2 p-3 text-sm text-gray-500 border rounded-md bg-gray-50">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Carregando cargos...
                </div>
              ) : isMobile ? (
                /* FIX MOBILE: <select> nativo para Cargo */
                <div className="relative">
                  <select
                    id="jobTitle"
                    value={jobTitle}
                    disabled={!sector}
                    onChange={(e) => {
                      const val = e.target.value;
                      setJobTitle(val);
                    }}
                    className="w-full h-12 px-3 pr-10 rounded-md border border-input bg-background text-sm appearance-none focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{ touchAction: 'manipulation', WebkitTapHighlightColor: 'transparent' }}
                  >
                    <option value="">{sector ? 'Selecione o cargo' : 'Selecione o setor primeiro'}</option>
                    {jobs.map((j: string) => (
                      <option key={j} value={j}>{j}</option>
                    ))}
                  </select>
                  <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center">
                    <svg className="h-4 w-4 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                  {/* Badge de confirmação visual quando selecionado */}
                  {jobTitle && (
                    <div className="mt-1.5 flex items-center gap-1.5 text-xs font-medium" style={{ color: branding.primaryColor }}>
                      <CheckIcon className="h-3.5 w-3.5" />
                      <span>Cargo selecionado: <strong>{jobTitle}</strong></span>
                    </div>
                  )}
                </div>
              ) : (
                /* Desktop: Radix UI Select (comportamento original) */
                <Select
                  value={jobTitle}
                  onValueChange={(value) => setJobTitle(value)}
                  disabled={!sector}
                >
                  <SelectTrigger id="jobTitle" ref={jobTitleTriggerRef}>
                    <SelectValue placeholder={sector ? "Selecione o cargo" : "Selecione o setor primeiro"} />
                  </SelectTrigger>
                  <SelectContent
                    className="z-[9999]"
                    position="popper"
                    sideOffset={4}
                  >
                    {jobs.length === 0 && sector && (
                      <SelectItem value="_empty" disabled>
                        Nenhum cargo disponível
                      </SelectItem>
                    )}
                    {jobs.map((j: string) => (
                      <SelectItem key={j} value={j}>{j}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              {!isLoadingJobs && sector && jobs.length === 0 && (
                <Alert className="mt-2">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    Nenhum cargo disponível para este setor.
                  </AlertDescription>
                </Alert>
              )}
            </div>

            {/* FIX MOBILE: Resumo de seleção — feedback inequívoco para o usuário */}
            {sector && jobTitle && (
              <div
                className="flex items-start gap-3 p-3 rounded-lg border-2 mt-2"
                style={{ borderColor: branding.primaryColor, backgroundColor: `${branding.primaryColor}0D` }}
              >
                <CheckCircle2 className="h-5 w-5 mt-0.5 flex-shrink-0" style={{ color: branding.primaryColor }} />
                <div className="text-sm">
                  <p className="font-semibold" style={{ color: branding.primaryColor }}>Identificação confirmada</p>
                  <p className="text-gray-600 mt-0.5">{sector} · {jobTitle}</p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Perguntas Iniciais (Informativas) */}
        {globalQuestionsList.length > 0 && (
          <Card
            id="pjc94-global"
            className={`mb-6 border-blue-200 bg-blue-50/30 ${showErrors && identOk && !globalOk ? "border-2 border-red-400" : ""} ${!identOk ? "opacity-60" : ""}`}
            aria-disabled={!identOk}
          >
            <CardHeader>
              <div className="flex items-center gap-2">
                <Info className="h-5 w-5 text-blue-600" />
                <CardTitle className="text-blue-900">Perguntas Iniciais</CardTitle>
              </div>
              <CardDescription className="text-blue-700">
                Estas perguntas nos ajudam a entender melhor o contexto do seu trabalho.
              </CardDescription>
              {!identOk && (
                <p className="text-xs text-amber-700 mt-1">Selecione setor e cargo para liberar estas perguntas.</p>
              )}
            </CardHeader>
            <CardContent className="space-y-6">
              {globalQuestionsList.map((question: any) => {
                const answerKey = `global_${question.code}`;
                const currentAnswer = answers[answerKey];

                if (question.answerType === 'OPEN_TEXT') {
                  const charCount = currentAnswer?.answerText?.length || 0;
                  const minLength = question.minLength || 0;
                  const maxLength = question.maxLength || 5000;
                  const isValid = charCount >= minLength && charCount <= maxLength;

                  return (
                    <div key={question.code} className="space-y-3">
                      <Label className="text-base font-medium text-gray-900">
                        {question.questionText}
                        {question.isRequired && <span className="text-red-500 ml-1">*</span>}
                      </Label>
                      <Textarea
                        value={currentAnswer?.answerText || ""}
                        onChange={(e) => {
                          const text = e.target.value;
                          setAnswers((prev) => ({
                            ...prev,
                            [answerKey]: {
                              moduleCode: "global",
                              questionCode: question.code,
                              answerScore: 0,
                              answerLabelSnapshot: text,
                              answerText: text,
                            },
                          }));
                        }}
                        placeholder="Descreva suas rotinas, processos e atividades..."
                        className="min-h-[120px] resize-y"
                        maxLength={maxLength}
                        disabled={!identOk}
                      />
                      <div className="flex items-center justify-between text-sm">
                        <span className={charCount < minLength ? "text-red-600" : "text-gray-600"}>
                          {charCount} / {maxLength} caracteres
                          {minLength > 0 && ` (mínimo: ${minLength})`}
                        </span>
                        {!isValid && charCount > 0 && (
                          <span className="text-red-600 text-xs">
                            {charCount < minLength
                              ? `Faltam ${minLength - charCount} caracteres`
                              : `Excede em ${charCount - maxLength} caracteres`}
                          </span>
                        )}
                      </div>
                    </div>
                  );
                }
                return null;
              })}
            </CardContent>
          </Card>
        )}

        {/* Barra de Progresso — cores dinâmicas */}
        <Card className="mb-6">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium">Progresso</span>
              <span className="text-sm text-gray-600">
                {answeredQuestions} de {totalQuestions} perguntas
              </span>
            </div>
            <div className="relative h-2 w-full overflow-hidden rounded-full" style={{ backgroundColor: `${branding.primaryColor}20` }}>
              <div
                className="h-full rounded-full transition-all duration-300"
                style={{
                  width: `${progress}%`,
                  backgroundColor: branding.primaryColor,
                }}
              />
            </div>
          </CardContent>
        </Card>

        {/* Módulo Atual — key dinâmica força unmount/remount completo ao trocar módulo,
            evitando race condition do Radix UI (removeChild) */}
        {currentModule && (
          <Card
            key={`module-${currentModuleIndex}-${currentModule.code}`}
            className={`mb-6 ${!modulesUnlocked ? "opacity-60" : ""}`}
            aria-disabled={!modulesUnlocked}
          >
            <CardHeader>
              <CardTitle>
                Módulo {currentModuleIndex + 1} de {modules.length}: {currentModule.name}
              </CardTitle>
              <CardDescription>{currentModule.description}</CardDescription>
              {!modulesUnlocked && (
                <p className="text-xs text-amber-700 mt-1">
                  {!identOk
                    ? "Selecione setor e cargo para liberar as perguntas."
                    : "Preencha as perguntas iniciais para liberar o módulo."}
                </p>
              )}
            </CardHeader>
            <CardContent className="space-y-6">
              {currentModule.questions?.map((question: any) => {
                const answerKey = `${currentModule.code}_${question.code}`;
                const currentAnswer = answers[answerKey];

                const isMissing = showErrors && modulesUnlocked && missingModuleCodes.has(question.code);

                if (question.answerType === 'OPEN_TEXT') {
                  return (
                    <div key={question.code} id={`q-${buildAnswerKey(currentModule.code, question.code)}`} className={`space-y-3 ${isMissing ? errorRing : ""}`}>
                      <Label className="text-base font-medium">
                        {question.questionText}
                        {question.isRequired && <span className="text-red-500 ml-1">*</span>}
                      </Label>
                      <Textarea
                        value={currentAnswer?.answerText || ""}
                        onChange={(e) => {
                          const text = e.target.value;
                          setAnswers({
                            ...answers,
                            [answerKey]: {
                              moduleCode: currentModule.code || "informative",
                              questionCode: question.code,
                              answerScore: 0,
                              answerLabelSnapshot: text.substring(0, 100),
                              answerText: text,
                            },
                          });
                        }}
                        placeholder="Digite sua resposta aqui..."
                        className="min-h-[120px] resize-y"
                        minLength={question.minLength || 10}
                        maxLength={question.maxLength || 2000}
                        disabled={!modulesUnlocked}
                      />
                      <p className="text-xs text-muted-foreground">
                        {currentAnswer?.answerText?.length || 0} / {question.maxLength || 2000} caracteres
                        {question.minLength && ` (mínimo: ${question.minLength})`}
                      </p>
                    </div>
                  );
                }

                // Pergunta fechada (CLOSED_LIST) — cores dinâmicas na seleção
                return (
                  <div key={question.code} id={`q-${buildAnswerKey(currentModule.code, question.code)}`} className={`space-y-3 ${isMissing ? errorRing : ""}`}>
                    <Label className="text-base font-medium">{question.questionText}</Label>
                    {question.description && (
                      <p className="text-sm text-muted-foreground italic -mt-1">{question.description}</p>
                    )}
                    {isMissing && <p className="text-sm text-red-600 font-medium">Resposta obrigatória</p>}
                    <RadioGroup
                      disabled={!modulesUnlocked}
                      value={currentAnswer?.answerScore?.toString() || ""}
                      onValueChange={(value) => {
                        const selectedOption = question.answers.find((o: any) => o.score === Number(value));
                        if (selectedOption) {
                          setAnswers({
                            ...answers,
                            [answerKey]: {
                              moduleCode: currentModule.code,
                              questionCode: question.code,
                              answerScore: selectedOption.score,
                              answerLabelSnapshot: selectedOption.label,
                            },
                          });
                        }
                      }}
                    >
                      {question.answers?.map((option: any) => {
                        const isSelected = currentAnswer?.answerScore === option.score;
                        return (
                          <Label
                            key={option.score}
                            htmlFor={`${answerKey}_${option.score}`}
                            className="flex items-center gap-3 p-4 rounded-lg border-2 cursor-pointer transition-all min-h-[56px]"
                            style={isSelected ? {
                              borderColor: branding.primaryColor,
                              backgroundColor: `${branding.primaryColor}08`,
                              boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                            } : {}}
                          >
                            <RadioGroupItem
                              value={option.score.toString()}
                              id={`${answerKey}_${option.score}`}
                              className="flex-shrink-0"
                              style={isSelected ? { borderColor: branding.primaryColor, color: branding.primaryColor } : {}}
                            />
                            <span className="text-sm md:text-base flex-1">{option.label}</span>
                          </Label>
                        );
                      })}
                    </RadioGroup>
                  </div>
                );
              })}
            </CardContent>
          </Card>
        )}

        {/* Navegação — botões com cores dinâmicas */}
        {showErrors && blocked && blockMessage && (
          <Alert variant="destructive" className="mb-4" role="alert">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{blockMessage}</AlertDescription>
          </Alert>
        )}
        <div className="flex items-center justify-between gap-4">
          <Button
            variant="outline"
            onClick={() => {
              setShowErrors(false);
              setBlockMessage(null);
              handleNavigate(Math.max(0, currentModuleIndex - 1));
            }}
            disabled={currentModuleIndex === 0 || isNavigating || isSubmitting}
          >
            Anterior
          </Button>

          <div className="flex gap-2">
            {currentModuleIndex < modules.length - 1 ? (
              <Button
                onClick={goNext}
                disabled={isNavigating || isSubmitting}
                aria-disabled={blocked}
                className={blocked ? "opacity-50 cursor-not-allowed" : ""}
                style={{ backgroundColor: branding.primaryColor, color: primaryFg }}
              >
                {isNavigating ? <Loader2 className="h-4 w-4 animate-spin" /> : "Próximo"}
              </Button>
            ) : (
              <Button
                onClick={submitIfValid}
                disabled={isSubmitting || submitSuccess || isNavigating}
                aria-disabled={blocked}
                className={blocked ? "opacity-50 cursor-not-allowed" : ""}
                style={{ backgroundColor: branding.primaryColor, color: primaryFg }}
              >
                {isSubmitting ? (
                  <span className="flex items-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Enviando...
                  </span>
                ) : "Enviar Respostas"}
              </Button>
            )}
          </div>
        </div>

        {/* Footer com branding */}
        <div className="mt-8 pt-4 border-t border-gray-200 text-center">
          <p className="text-xs text-gray-400">
            Powered by{' '}
            <span className="font-medium" style={{ color: branding.primaryColor }}>
              {branding.channelName || 'Salvia Insight'}
            </span>
          </p>
        </div>
      </div>
    </div>

    {/* Modal: Questionário já respondido */}
    <Dialog open={showAlreadyAnsweredModal} onOpenChange={setShowAlreadyAnsweredModal}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertCircle className="h-5 w-5 text-amber-500" />
            Questionário já respondido
          </DialogTitle>
          <DialogDescription className="pt-4 space-y-3">
            <p>
              Identificamos que este questionário já foi respondido neste dispositivo.
            </p>
            <p>
              Para evitar duplicidade de respostas, não é possível reenviar.
            </p>
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button
            onClick={() => setShowAlreadyAnsweredModal(false)}
            className="w-full"
            style={{ backgroundColor: branding.primaryColor, color: primaryFg }}
          >
            OK
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    </>
  );
}
